// "Frequently bought together" strip in the cart drawer, below the free gift bar.
//
// Flow: the cart drawer renders <div data-cart-fbt data-skus="..."> from the
// cart items' SKUs. The SKU is the same ID the backend uses as `product_id`,
// so we POST the SKUs to the FBT API, then resolve each returned ID back to a
// Shopify product via the storefront search (templates/search.fbt.liquid,
// matched on exact variant SKU). The drawer HTML is replaced on every cart
// change, so a MutationObserver re-fills each fresh placeholder (results are
// cached, so re-renders are instant).
(function () {
  'use strict';

  var API_URL = 'https://nodejs.animeal.in/products/get-cart-drawer-frequently-bought-together-cached';
  var MAX_LOOKUPS = 10;
  var MAX_SHOWN = 10;
  // Backend order webhook records a line item as an FBT sale when it carries
  // `_fbt_source` with one of these values (from the API row's `source`).
  var FBT_SOURCES = ['same_order', 'manual', 'cross_order'];

  var apiCache = {};
  var productCache = {};
  var htmlCache = {};
  var renderToken = 0;

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function fetchRecommendedIds(skus) {
    var key = skus.join(',');
    if (!apiCache[key]) {
      apiCache[key] = fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_ids: skus.map(Number) }),
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (json) {
          return json && json.success && Array.isArray(json.data)
            ? json.data.map(function (row) {
                return { id: String(row.product_id), source: row.source };
              })
            : [];
        })
        .catch(function () {
          delete apiCache[key];
          return [];
        });
    }
    return apiCache[key];
  }

  function fetchProductBySku(sku) {
    if (!productCache[sku]) {
      var url = '/search?view=fbt&type=product&q=' + encodeURIComponent('variants.sku:' + sku);
      productCache[sku] = fetch(url, { credentials: 'same-origin' })
        .then(function (response) {
          return response.json();
        })
        .catch(function () {
          delete productCache[sku];
          return null;
        });
    }
    return productCache[sku];
  }

  function cardHtml(product, source, anchor) {
    var price = product.on_sale
      ? '<s class="cart-fbt__compare">' + escapeHtml(product.compare_at_price) + '</s> '
      : '';
    return (
      '<div class="cart-fbt__card">' +
      '<a class="cart-fbt__image-link" href="' + escapeHtml(product.url) + '">' +
      (product.image
        ? '<img class="cart-fbt__image" src="' + escapeHtml(product.image) + '" alt="' + escapeHtml(product.title) + '" width="80" height="80" loading="lazy">'
        : '') +
      '</a>' +
      '<div class="cart-fbt__info">' +
      '<a class="cart-fbt__name" href="' + escapeHtml(product.url) + '">' + escapeHtml(product.title) + '</a>' +
      '<p class="cart-fbt__price">' + price + '<span>' + escapeHtml(product.price) + '</span></p>' +
      '</div>' +
      '<button type="button" class="cart-fbt__add" data-cart-fbt-add data-variant-id="' + product.variant_id + '" data-quantity="' + (product.quantity || 1) + '"' +
      (FBT_SOURCES.indexOf(source) !== -1 ? ' data-fbt-source="' + source + '"' : '') +
      (anchor ? ' data-fbt-anchor="' + escapeHtml(anchor) + '"' : '') +
      '>Add</button>' +
      '</div>'
    );
  }

  function fill(el) {
    var skus = (el.dataset.skus || '')
      .split(',')
      .map(function (s) {
        return s.trim();
      })
      .filter(function (s) {
        return /^\d+$/.test(s);
      });
    if (!skus.length) return;

    var inCart = (el.dataset.cartVariantIds || '').split(',').filter(Boolean);
    // `_fbt_anchor` is only meaningful when a single product is in the cart
    var anchor = skus.length === 1 && inCart.length === 1 ? skus[0] : '';
    var token = ++renderToken;
    el.dataset.fbtToken = token;

    // The drawer HTML is replaced on every cart change; show the last result
    // right away so the strip doesn't vanish while it is being re-fetched.
    var cacheKey = skus.join(',') + '|' + inCart.join(',');
    if (htmlCache[cacheKey]) mount(el, htmlCache[cacheKey]);

    fetchRecommendedIds(skus)
      .then(function (rows) {
        rows = rows
          .filter(function (row) {
            return skus.indexOf(row.id) === -1;
          })
          .slice(0, MAX_LOOKUPS);
        return Promise.all(rows.map(function (row) {
          return fetchProductBySku(row.id);
        })).then(function (products) {
          return products.map(function (p, i) {
            var reason = !p ? 'not found by SKU in Shopify search' : !p.available ? 'out of stock' : inCart.indexOf(String(p.variant_id)) !== -1 ? 'already in cart' : 'shown';
            console.info('[cart-fbt] API id ' + rows[i].id + ' (source: ' + rows[i].source + ') -> ' + reason + (p ? ' (' + p.title + ')' : ''));
            if (FBT_SOURCES.indexOf(rows[i].source) === -1) {
              console.warn('[cart-fbt] API id ' + rows[i].id + ' has invalid source "' + rows[i].source + '"; it will not be tracked as an FBT sale');
            }
            return { product: p, source: rows[i].source };
          });
        });
      })
      .then(function (results) {
        if (!el.isConnected || el.dataset.fbtToken !== String(token)) return;
        var items = results
          .filter(function (r) {
            var p = r.product;
            return p && p.available && inCart.indexOf(String(p.variant_id)) === -1;
          })
          .slice(0, MAX_SHOWN)
          .sort(function (a, b) {
            return (a.product.price_cents || 0) - (b.product.price_cents || 0);
          });
        if (!items.length) return;

        var arrows = items.length > 2
          ? '<div class="cart-fbt__nav">' +
            '<button type="button" class="cart-fbt__arrow" data-cart-fbt-nav="-1" aria-label="Previous products" disabled>&#8249;</button>' +
            '<button type="button" class="cart-fbt__arrow" data-cart-fbt-nav="1" aria-label="Next products">&#8250;</button>' +
            '</div>'
          : '';
        var html =
          '<div class="cart-fbt__head"><p class="cart-fbt__heading">Frequently bought together</p>' + arrows + '</div>' +
          '<div class="cart-fbt__list">' + items.map(function (r) {
            return cardHtml(r.product, r.source, anchor);
          }).join('') + '</div>';
        htmlCache[cacheKey] = html;
        mount(el, html);
      });
  }

  function mount(el, html) {
    el.innerHTML = html;
    var list = el.querySelector('.cart-fbt__list');
    list.addEventListener('scroll', function () {
      updateArrows(el);
    });
    el.hidden = false;
    updateArrows(el);
  }

  function updateArrows(el) {
    var list = el.querySelector('.cart-fbt__list');
    var prev = el.querySelector('[data-cart-fbt-nav="-1"]');
    var next = el.querySelector('[data-cart-fbt-nav="1"]');
    if (!list || !prev || !next) return;
    // Hide the arrows when everything already fits (3 per view on desktop, 2 on mobile)
    prev.parentNode.hidden = list.scrollWidth <= list.clientWidth + 1;
    prev.disabled = list.scrollLeft <= 1;
    next.disabled = list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;
  }

  // Arrows move the list by one "page" (3 products at a time)
  document.addEventListener('click', function (event) {
    var arrow = event.target.closest('[data-cart-fbt-nav]');
    if (!arrow || arrow.disabled) return;
    var list = arrow.closest('[data-cart-fbt]').querySelector('.cart-fbt__list');
    if (!list) return;
    list.scrollBy({ left: Number(arrow.dataset.cartFbtNav) * list.clientWidth, behavior: 'smooth' });
  });

  // Mouse drag-to-scroll (touch already scrolls natively)
  (function () {
    var list = null, startX = 0, startLeft = 0, moved = false;

    document.addEventListener('mousedown', function (event) {
      if (event.button !== 0) return;
      var target = event.target.closest('.cart-fbt__list');
      if (!target) return;
      list = target;
      startX = event.clientX;
      startLeft = list.scrollLeft;
      moved = false;
    });

    document.addEventListener('mousemove', function (event) {
      if (!list) return;
      var dx = event.clientX - startX;
      if (!moved && Math.abs(dx) < 5) return;
      if (!moved) {
        moved = true;
        list.classList.add('is-dragging');
      }
      event.preventDefault();
      list.scrollLeft = startLeft - dx;
    });

    function end() {
      if (!list) return;
      var l = list;
      list = null;
      if (!moved) return;
      l.classList.remove('is-dragging');
      // Snap to the nearest card after release
      var card = l.querySelector('.cart-fbt__card');
      if (card) {
        var step = card.offsetWidth + parseFloat(getComputedStyle(l).columnGap || 0);
        l.scrollTo({ left: Math.round(l.scrollLeft / step) * step, behavior: 'smooth' });
      }
    }
    document.addEventListener('mouseup', end);
    document.addEventListener('mouseleave', end);

    // Don't trigger links/buttons when the mouse-up ends a drag
    document.addEventListener('click', function (event) {
      if (moved && event.target.closest('.cart-fbt__list')) {
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      }
    }, true);

    document.addEventListener('dragstart', function (event) {
      if (event.target.closest && event.target.closest('.cart-fbt__list')) event.preventDefault();
    });
  })();

  function scan() {
    document.querySelectorAll('[data-cart-fbt]:not([data-fbt-init])').forEach(function (el) {
      el.setAttribute('data-fbt-init', '');
      fill(el);
    });
  }

  /* ------------------------------------------------------------------ *
   * Adding a product
   * ------------------------------------------------------------------ */

  function applySections(sectionData) {
    if (window.customATCManager && typeof window.customATCManager.updateCartSectionsFromData === 'function') {
      window.customATCManager.updateCartSectionsFromData(sectionData, true);
      return;
    }
    if (!sectionData || !sectionData['cart-drawer']) return;
    var target = document.querySelector('#CartDrawer');
    var source = new DOMParser().parseFromString(sectionData['cart-drawer'], 'text/html').querySelector('#CartDrawer');
    if (target && source) target.innerHTML = source.innerHTML;
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-cart-fbt-add]');
    if (!button || button.disabled) return;
    event.preventDefault();

    button.disabled = true;
    button.textContent = 'Adding...';

    var fbtDoctorId = null;
    try { fbtDoctorId = localStorage.getItem('doctor_id'); } catch (e) {}
    var fbtItem = { id: parseInt(button.dataset.variantId, 10), quantity: parseInt(button.dataset.quantity, 10) || 1 };
    var properties = {};
    if (fbtDoctorId) properties.doctorId = fbtDoctorId;
    if (FBT_SOURCES.indexOf(button.dataset.fbtSource) !== -1) {
      properties._fbt_source = button.dataset.fbtSource;
      if (button.dataset.fbtAnchor) properties._fbt_anchor = button.dataset.fbtAnchor;
    }
    if (Object.keys(properties).length) fbtItem.properties = properties;

    fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [fbtItem],
        sections: ['cart-drawer', 'cart-icon-bubble', 'main-cart-items', 'main-cart-footer'],
        sections_url: window.location.pathname,
      }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        if (response.status) throw new Error(response.description || response.message);
        applySections(response.sections);
        document.dispatchEvent(new CustomEvent('custom-cart-update'));
        window.dispatchEvent(new CustomEvent('custom-atc-updated'));
        if (window.customATCManager && typeof window.customATCManager.refreshCartData === 'function') {
          window.customATCManager.refreshCartData(true);
        }
      })
      .catch(function (error) {
        console.error('Error adding frequently bought product:', error);
        button.disabled = false;
        button.textContent = 'Add';
      });
  });

  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }
})();
